import React, { createContext, useContext } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "./yupResolver";

/**
 * <Form/> is the single entry point for every data-entry surface in the app.
 *
 *   <Form schema={createCustomerSchema} defaultValues={initial} onSubmit={save}>
 *     <Field name="companyName" label="Company" required />
 *     <Field name="customerType" type="select" options={customerTypeOptions} />
 *     <FormSubmit>Save</FormSubmit>
 *   </Form>
 *
 * - `schema` (yup) drives validation. Optional — omit for free-form layouts.
 * - `defaultValues` populate the form. Updates to this prop after mount are
 *   NOT picked up automatically (RHF behaviour); use `reset()` from a ref or
 *   recreate the form by keying its parent.
 * - `onSubmit(values, helpers)` is called only on valid submission. `helpers`
 *   exposes `{ reset, setError, setValue, getValues }` so callers don't need
 *   to crack open the RHF API for common cases.
 * - `mode='onTouched'` matches users' expectations: errors surface once a
 *   field is touched, not on every keystroke (less noisy than `onChange`)
 *   and not too late (better than `onSubmit`).
 *
 * Wraps RHF's FormProvider so any descendant can call useFormCtx() / useFormContext().
 */
const ExtraCtx = createContext({ submitting: false });
export const useFormCtx = () => useContext(ExtraCtx);

const Form = ({
  schema,
  defaultValues,
  onSubmit,
  mode = "onTouched",
  className = "space-y-4",
  children,
}) => {
  const methods = useForm({
    defaultValues,
    mode,
    resolver: schema ? yupResolver(schema) : undefined,
  });

  const submit = methods.handleSubmit(async (values) => {
    await onSubmit?.(values, {
      reset: methods.reset,
      setError: methods.setError,
      setValue: methods.setValue,
      getValues: methods.getValues,
    });
  });

  return (
    <FormProvider {...methods}>
      <ExtraCtx.Provider value={{ submitting: methods.formState.isSubmitting }}>
        <form onSubmit={submit} className={className} noValidate>
          {children}
        </form>
      </ExtraCtx.Provider>
    </FormProvider>
  );
};

export default Form;
